import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';

import type { PageQuery, PagedResult } from '@domain/models/paged-result.model';
import type {
  SignupAttempt,
  User,
  UserFilter,
  UserTransfer,
  UserWallet,
} from '@domain/models/user.model';
import { UserRepository } from '@domain/repositories/user.repository';

import type {
  ApiPagedResponse,
  SignupAttemptDto,
  UserDto,
  UserTransferDto,
  UserWalletDto,
} from '@data/dto/api.dto';
import {
  mapPagedSignupAttempts,
  mapPagedTransfers,
  mapPagedUsers,
  mapUserDto,
  mapUserWalletDto,
  toPageParams,
} from '@data/mappers/user.mapper';

@Injectable()
export class UserApiRepository extends UserRepository {
  private readonly http = inject(HttpClient);
  private readonly path = 'api/Users';

  list(query: PageQuery, filter?: UserFilter): Observable<PagedResult<User>> {
    let params = new HttpParams({ fromObject: toPageParams(query) as Record<string, string | number> });

    return this.http.get<ApiPagedResponse<UserDto>>(this.path, { params }).pipe(
      map(mapPagedUsers),
      map((result) => this.applyClientFilter(result, filter)),
    );
  }

  listCancelRequests(query: PageQuery, active = true): Observable<PagedResult<User>> {
    const params = new HttpParams({
      fromObject: {
        ...toPageParams(query),
        active: active ? 'true' : 'false',
      },
    });

    return this.http
      .get<ApiPagedResponse<UserDto>>(`${this.path}/GetUsersCancelWithReason`, { params })
      .pipe(map(mapPagedUsers));
  }

  listSignupAttempts(query: PageQuery): Observable<PagedResult<SignupAttempt>> {
    const params = new HttpParams({
      fromObject: toPageParams(query) as Record<string, string | number>,
    });

    return this.http
      .get<ApiPagedResponse<SignupAttemptDto>>(`${this.path}/GetUsersLogNotHaveAccount`, {
        params,
      })
      .pipe(map(mapPagedSignupAttempts));
  }

  getById(id: number): Observable<User> {
    return this.http.get<UserDto>(`${this.path}/${id}`).pipe(map(mapUserDto));
  }

  getWallet(id: number): Observable<UserWallet> {
    return this.http.get<UserWalletDto>(`${this.path}/${id}/wallet`).pipe(map(mapUserWalletDto));
  }

  getTransfers(id: number, query: PageQuery): Observable<PagedResult<UserTransfer>> {
    const params = new HttpParams({
      fromObject: toPageParams(query) as Record<string, string | number>,
    });

    return this.http
      .get<ApiPagedResponse<UserTransferDto>>(`${this.path}/${id}/transfers`, { params })
      .pipe(map(mapPagedTransfers));
  }

  updateTag(userId: number, userTagId: number): Observable<void> {
    return this.http.put<void>(`${this.path}/${userId}/tag`, { userTagId });
  }

  resetPin(userId: number, pin: string): Observable<void> {
    return this.http.put<void>(`${this.path}/UpdatePin/${userId}`, { pin });
  }

  addAdminCredit(userId: number, amount: number): Observable<void> {
    return this.http.post<void>('api/admin-credits', { userId, amount });
  }

  softDelete(userId: number): Observable<void> {
    return this.http.put<void>(`api/Employees/${userId}/Delete`, {});
  }

  /**
   * API currently pages server-side without rich filters; apply common list filters
   * on the current page (same approach as the old LocalDataSource filters).
   */
  private applyClientFilter(
    result: PagedResult<User>,
    filter?: UserFilter,
  ): PagedResult<User> {
    if (!filter) {
      return result;
    }

    let items = [...result.items];

    if (filter.search?.trim()) {
      const term = filter.search.trim().toLowerCase();
      items = items.filter(
        (u) =>
          u.name.toLowerCase().includes(term) ||
          (u.email?.toLowerCase().includes(term) ?? false) ||
          (u.phoneNumber?.includes(term) ?? false) ||
          String(u.id).includes(term),
      );
    }

    if (filter.tagName?.trim()) {
      const tag = filter.tagName.trim().toLowerCase();
      items = items.filter((u) => u.userTagName.toLowerCase().includes(tag));
    }

    if (filter.hasStore === true) {
      items = items.filter((u) => u.storeCount > 0);
    } else if (filter.hasStore === false) {
      items = items.filter((u) => u.storeCount === 0);
    }

    if (filter.neverActive) {
      items = items.filter((u) => u.lastActive == null);
    }

    if (filter.createdFrom) {
      const from = new Date(filter.createdFrom);
      items = items.filter((u) => u.createdOn >= from);
    }

    if (filter.createdTo) {
      const to = new Date(filter.createdTo);
      to.setHours(23, 59, 59, 999);
      items = items.filter((u) => u.createdOn <= to);
    }

    if (filter.lastActiveFrom) {
      const from = new Date(filter.lastActiveFrom);
      items = items.filter((u) => u.lastActive != null && u.lastActive >= from);
    }

    if (filter.lastActiveTo) {
      const to = new Date(filter.lastActiveTo);
      to.setHours(23, 59, 59, 999);
      items = items.filter((u) => u.lastActive != null && u.lastActive <= to);
    }

    return {
      ...result,
      items,
      total: filter.search || filter.tagName || filter.hasStore != null || filter.neverActive
        || filter.createdFrom || filter.createdTo || filter.lastActiveFrom || filter.lastActiveTo
        ? items.length
        : result.total,
    };
  }
}
